import apiService from "@/apiService";

/**
 * Permissions store: single source of truth for the user's accessible modules.
 *
 * Why a store and not raw localStorage:
 *  - Reactivity: components react automatically when modules change.
 *  - Status semantics: distinguishes "not loaded yet" from "loaded but empty"
 *    (the latter == super-admin per backend contract).
 *  - Concurrency: dedupes overlapping fetches (router + sidebar + quick-access
 *    can all call ensureLoaded() in parallel; only one HTTP call goes out).
 *  - LocalStorage is only a *bootstrap cache* for hard reloads; the store stays
 *    authoritative at runtime.
 *
 * Security note: client-side permission data can always be tampered with.
 * Backend MUST enforce authorization independently — this is UX only.
 */

const LS_MODULES = "user_modules";
const LS_TYPE = "user_modules_type";

function loadFromStorage() {
    try {
        const raw = localStorage.getItem(LS_MODULES);
        const type = localStorage.getItem(LS_TYPE);
        if (!raw) return { modules: [], type: null, hasCache: false };
        const modules = JSON.parse(raw);
        if (!Array.isArray(modules)) {
            return { modules: [], type: null, hasCache: false };
        }
        return { modules, type, hasCache: true };
    } catch (e) {
        return { modules: [], type: null, hasCache: false };
    }
}

function persistToStorage(modules, type) {
    try {
        localStorage.setItem(LS_MODULES, JSON.stringify(modules));
        if (type) localStorage.setItem(LS_TYPE, type);
    } catch (e) {
        // quota exceeded etc. — non-fatal
    }
}

function clearStorage() {
    try {
        localStorage.removeItem(LS_MODULES);
        localStorage.removeItem(LS_TYPE);
    } catch (e) {
        // non-fatal
    }
}

const bootstrap = loadFromStorage();

export default {
    namespaced: true,
    state: () => ({
        modules: bootstrap.modules,
        loadedForType: bootstrap.type,
        // 'idle' | 'loading' | 'loaded' | 'error'
        status: bootstrap.hasCache ? "loaded" : "idle",
        // Holds the in-flight promise so concurrent calls can dedupe.
        // Marked non-reactive intentionally (no UI binds to it).
        _inflight: null,
    }),
    getters: {
        isLoaded: (s) => s.status === "loaded",
        isLoading: (s) => s.status === "loading",
        // Backend contract: empty modules array == super-admin / no restrictions.
        // Only meaningful once status is "loaded".
        isSuperAdmin: (s) =>
            s.status === "loaded" && (!s.modules || s.modules.length === 0),
        // Set of every concrete link the user is allowed to visit.
        allowedLinks(state) {
            const set = new Set();
            (state.modules || []).forEach((m) => {
                if (m.link && m.link !== "-") {
                    set.add(String(m.link).toLowerCase());
                }
                (m.submodule || []).forEach((sub) => {
                    if (sub && sub.link && sub.link !== "-") {
                        set.add(String(sub.link).toLowerCase());
                    }
                });
            });
            return set;
        },
    },
    mutations: {
        SET_STATUS(s, v) {
            s.status = v;
        },
        SET_MODULES(s, { modules, type }) {
            s.modules = Array.isArray(modules) ? modules : [];
            s.loadedForType = type || null;
            s.status = "loaded";
        },
        SET_INFLIGHT(s, v) {
            s._inflight = v;
        },
        CLEAR(s) {
            s.modules = [];
            s.loadedForType = null;
            s.status = "idle";
            s._inflight = null;
        },
    },
    actions: {
        /**
         * Load modules for projectType if not already cached for that type.
         * Concurrent callers share the same in-flight request.
         */
        ensureLoaded({ state, dispatch }, projectType) {
            if (
                state.status === "loaded" &&
                state.loadedForType === projectType
            ) {
                return Promise.resolve(state.modules);
            }
            if (state._inflight) return state._inflight;
            return dispatch("reload", projectType);
        },
        /**
         * Force-fetch modules for projectType. Replaces cache on success.
         */
        reload({ state, commit }, projectType) {
            if (state._inflight) return state._inflight;

            commit("SET_STATUS", "loading");
            const promise = (async () => {
                try {
                    const response = await apiService("modules", "get", {
                        type: projectType,
                    });
                    if (response && response.status !== false) {
                        const modules = response?.data?.modules ?? [];
                        commit("SET_MODULES", { modules, type: projectType });
                        persistToStorage(modules, projectType);
                        return modules;
                    }
                    commit("SET_STATUS", "error");
                    return [];
                } catch (e) {
                    commit("SET_STATUS", "error");
                    return [];
                } finally {
                    commit("SET_INFLIGHT", null);
                }
            })();
            commit("SET_INFLIGHT", promise);
            return promise;
        },
        /**
         * Drop everything (logout / project deselected).
         */
        clear({ commit }) {
            commit("CLEAR");
            clearStorage();
        },
    },
};
